import { fadeIn, fadeOut, hide, isVisible, show, slideDown, slideUp } from "./Helpers/animations";
import { initCommonHandlers } from "./Partials/CommonHandlers";
import { initPrintHandlers } from "./Partials/Print";
import { initTranslationHandlers } from "./Partials/Translation";

const mainMenuToggle = document.querySelector('header .toggle-menu') as HTMLButtonElement | null;
const searchMenuToggle = document.querySelector('header .toggle-search') as HTMLButtonElement | null;
const dropdownContainer = document.querySelector('.dropdowns') as HTMLDivElement | null;
const dropdowns = document.querySelectorAll('.dropdowns .dropdown') as NodeListOf<HTMLDivElement>;
const mobileMainNav = document.getElementById('dropdown-0') as HTMLDivElement | null;
const mobileSearch = document.getElementById('dropdown-search') as HTMLElement | null;
const header = document.querySelector('#page header') as HTMLElement | null;

const initMenuToggles = () => {
    mainMenuToggle?.addEventListener('click', () => {
        if (mainMenuToggle.classList.contains('opened')) {
            mainMenuToggle.classList.remove('opened');
            mainMenuToggle.ariaExpanded = 'false';
            document.documentElement.classList.remove('behind');

            fadeOut(dropdownContainer, 200, { removeClasses: ['opened'] });
            dropdowns.forEach(dropdown => fadeOut(dropdown, 200));
        } else {
            mainMenuToggle.classList.add('opened');
            mainMenuToggle.ariaExpanded = 'true';
            document.documentElement.classList.add('behind');

            if (searchMenuToggle?.classList.contains('opened')) {
                searchMenuToggle.classList.remove('opened');
                searchMenuToggle.ariaExpanded = 'false';

                fadeOut(mobileSearch, 200, {
                    callback: () => {
                        dropdowns.forEach(dropdown => hide(dropdown));
                        fadeIn(mobileMainNav, 200, {
                            callback: () => (mobileMainNav?.querySelector('button, a') as HTMLButtonElement | HTMLAnchorElement | null)?.focus()
                        });
                    }
                });
            } else {
                fadeIn(dropdownContainer, 200, { addClasses: ['opened'] });
                fadeIn(mobileMainNav, 200, {
                    callback: () => (mobileMainNav?.querySelector('button, a') as HTMLButtonElement | HTMLAnchorElement | null)?.focus()
                });
            }
        }
    });

    searchMenuToggle?.addEventListener('click', () => {
        if (searchMenuToggle.classList.contains('opened')) {
            searchMenuToggle.classList.remove('opened');
            searchMenuToggle.ariaExpanded = 'false';

            fadeOut(mobileSearch, 200, {
                callback: () => {
                    document.documentElement.classList.remove('behind');
                    searchMenuToggle.focus();
                }
            });
            fadeOut(dropdownContainer, 200, { removeClasses: ['opened'] });
        } else {
            searchMenuToggle.classList.add('opened');
            searchMenuToggle.ariaExpanded = 'true';

            document.documentElement.classList.add('behind');

            if (mainMenuToggle?.classList.contains('opened')) {
                mainMenuToggle.classList.remove('opened');
                mainMenuToggle.ariaExpanded = 'false';

                dropdowns.forEach(dropdown => fadeOut(dropdown));

                setTimeout(() => fadeIn(mobileSearch, 200, {
                    callback: () => document.getElementById('search-terms-mobile')?.focus() 
                }), 200);
            } else {
                dropdowns.forEach(dropdown => hide(dropdown));
                fadeIn(dropdownContainer, 200, { addClasses: ['opened'] });
                fadeIn(mobileSearch, 200, {
                    callback: () => document.getElementById('search-terms-mobile')?.focus()
                });
            }
        }
    });
};

const resetMobileMenus = () => {
    document.documentElement.classList.remove('behind');
    document.body.classList.remove('behind');
    
    if (mainMenuToggle) {
        mainMenuToggle.classList.remove('opened');
        mainMenuToggle.ariaExpanded = 'false';
    }

    if (searchMenuToggle) {
        searchMenuToggle.classList.remove('opened');
        searchMenuToggle.ariaExpanded = 'false';
    }

    dropdowns.forEach(dropdown => hide(dropdown));
    hide(dropdownContainer, { removeClasses: ['opened'] });
};

const initMobileMenuResetOnResizeHandler = () => {
    window.addEventListener('resize', () => window.innerWidth > 991 && resetMobileMenus());
};

const fadeOutMobileMenus = () => {
    document.documentElement.classList.remove('behind');
    document.body.classList.remove('behind');
    
    if (mainMenuToggle) {
        mainMenuToggle.classList.remove('opened');
        mainMenuToggle.ariaExpanded = 'false';
    }

    if (searchMenuToggle) {
        searchMenuToggle.classList.remove('opened');
        searchMenuToggle.ariaExpanded = 'false';
    }

    dropdowns.forEach(dropdown => fadeOut(dropdown, 200));
    fadeOut(dropdownContainer, 200, {
        callback: () => dropdownContainer?.classList.remove('opened')
    });
};

const initDropdownTogglesHandler = () => {
    const desktopDropdownsToggles = document.querySelectorAll('header .links-dropdowns button') as NodeListOf<HTMLButtonElement>;
    desktopDropdownsToggles.forEach(button => button.addEventListener('click', () => {
        button.ariaExpanded = 'true';
        document.body.classList.add('behind');
        hide(mobileSearch);
        show(dropdownContainer, { addClasses: ['opened'] });

        const controlledDropdown = button.ariaControlsElements?.[0] as HTMLElement | undefined;
        fadeIn(controlledDropdown, 300, {
            callback: () => (controlledDropdown?.querySelector('.close-dropdowns') as HTMLAnchorElement)?.focus()
        });
    }));

    const mobileDropdownToggles = document.querySelectorAll('.dropdowns .links-dropdowns button') as NodeListOf<HTMLButtonElement>;
    mobileDropdownToggles.forEach(button => button.addEventListener('click', event => {
        button.ariaExpanded = 'true';
        dropdowns.forEach(dropdown => hide(dropdown));
        fadeIn(button.ariaControlsElements?.[0] as HTMLElement | undefined, 300, {
            callback: () => (button.ariaControlsElements?.[0].querySelector('button, a') as HTMLButtonElement | HTMLAnchorElement | null)?.focus()
        });
    }));

    document.querySelectorAll('.dropdowns .close-dropdowns').forEach(button => button.addEventListener('click', () => {
        desktopDropdownsToggles.forEach(toggle => toggle.ariaExpanded = 'false');
        mobileDropdownToggles.forEach(toggle => toggle.ariaExpanded = 'false');
        fadeOutMobileMenus();

        const displayedDropdown = [...dropdowns].find(dropdown => isVisible(dropdown));
        if (displayedDropdown) {
            (document.querySelector(`button[aria-controls=${displayedDropdown.id}`) as HTMLButtonElement | null)?.focus();
        }
    }));

    document.querySelectorAll('.dropdowns .toggle-list').forEach(toggle => toggle.addEventListener('click', () => {
        if (toggle.ariaExpanded === 'true') {
            toggle.ariaExpanded = 'false';
            toggle.classList.remove('opened');
            slideUp(toggle.parentElement?.nextElementSibling as HTMLElement, 200);
        } else {
            toggle.ariaExpanded = 'true';
            toggle.classList.add('opened');
            slideDown(toggle.parentElement?.nextElementSibling as HTMLElement, 200);
        }
    }));
};

const initDropdownFocusTraps = () => {
    header?.addEventListener('focusout', event => {
        if (dropdownContainer?.classList.contains('opened')) {
            const displayedDropdown = [...dropdowns].find(dropdown => isVisible(dropdown));

            if (!event.relatedTarget || (!displayedDropdown?.contains(event.relatedTarget as HTMLElement) && !header.contains(event.relatedTarget as HTMLElement))) {
                mainMenuToggle?.focus();
            }
        }
    });

    header?.addEventListener('keydown', event => {
        if (dropdownContainer?.classList.contains('opened') && event.key === 'Escape') {
            event.preventDefault();
            const displayedDropdown = [...dropdowns].find(dropdown => isVisible(dropdown));
            fadeOutMobileMenus();

            if (window.innerWidth < 992) {
                mainMenuToggle?.focus();
            } else if (displayedDropdown) {
                (document.querySelector(`button[aria-controls=${displayedDropdown.id}`) as HTMLButtonElement | null)?.focus();
            }
        }
    });

    mainMenuToggle?.addEventListener('keydown', event => {
        if (dropdownContainer?.classList.contains('opened') && event.key === 'Tab' && event.shiftKey) {
            event.preventDefault();
            const displayedDropdown = [...dropdowns].find(dropdown => isVisible(dropdown));
            const currentTargets = displayedDropdown?.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
            [...currentTargets].findLast(target => isVisible(target))?.focus();
        }
    });

    searchMenuToggle?.addEventListener('keydown', event => {
        if (dropdownContainer?.classList.contains('opened')) {
            const displayedDropdown = [...dropdowns].find(dropdown => isVisible(dropdown));

            if (event.key === 'Tab' && !event.shiftKey) {
                event.preventDefault();
                (displayedDropdown?.querySelector('a, button, input') as HTMLAnchorElement | HTMLButtonElement | HTMLInputElement | null)?.focus();
            }
        }
    });

    dropdowns.forEach(dropdown => {
        dropdown.addEventListener('keydown', ((event: KeyboardEvent) => {
            if (dropdownContainer?.classList.contains('opened')) {
                if (window.innerWidth < 992) {
                    if (event.key === 'Tab') {
                        const currentTargets = dropdown?.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                        const lastVisible = [...currentTargets].findLast(element => isVisible(element));
                        const firstVisible = [...currentTargets].find(element => isVisible(element));

                        if (event.shiftKey && event.target === firstVisible) {
                            event.preventDefault();
                            searchMenuToggle?.focus();
                        } else if (!event.shiftKey && event.target === lastVisible) {
                            event.preventDefault();
                            mainMenuToggle?.focus();
                        }
                    } else if (event.key === 'Escape') {
                        event.preventDefault();
                        fadeOutMobileMenus();
                        mainMenuToggle?.focus();
                    }
                } else {
                    if (event.key === 'Tab') {
                        const currentTargets = dropdown?.querySelectorAll('a, button, input') as NodeListOf<HTMLAnchorElement | HTMLButtonElement | HTMLInputElement>;
                        const lastVisible = [...currentTargets].findLast(element => isVisible(element));
                        const firstVisible = [...currentTargets].find(element => isVisible(element));

                        if (event.shiftKey && event.target === firstVisible) {
                            event.preventDefault();
                            lastVisible?.focus();
                        } else if (!event.shiftKey && event.target === lastVisible) {
                            event.preventDefault();
                            firstVisible?.focus();
                        }
                    } else if (event.key === 'Escape') {
                        event.preventDefault();
                        fadeOutMobileMenus();
                        (document.querySelector(`button[aria-controls=${dropdown.id}`) as HTMLButtonElement | null)?.focus();
                    }
                }
            }
        }) as EventListener);

        dropdown.addEventListener('focusout', event => {
            if (!event.relatedTarget || (!dropdown.contains(event.relatedTarget as HTMLElement) && !header?.contains(event.relatedTarget as HTMLElement))) {
                mainMenuToggle?.focus();
            }
        });
    });
};

initMenuToggles();
initMobileMenuResetOnResizeHandler();
initDropdownTogglesHandler();
initDropdownFocusTraps();
initCommonHandlers();
initTranslationHandlers();
initPrintHandlers();